import { inject, Injectable } from '@angular/core';

import { IntelliConfigService } from './intelli-config.service';
import { IntelliContextService } from './intelli-context.service';
import { IntelliDataService } from './intelli-data.service';
import { IntelliDropdownOptionsService } from './intelli-dropdown-options.service';
import { IntelliExpressionsService } from './intelli-expressions.service';
import { IntelliQueryService } from './intelli-query.service';
import { IntelliRuntimeContextService } from './intelli-runtime-context.service';
import { WorkflowSchemaCalculatorService } from './workflow-services/workflow-schema-calculator.service';

@Injectable({ providedIn: 'root' })
export class IntelliSenseService {
  private readonly iConfigService = inject(IntelliConfigService);
  private readonly iContextService = inject(IntelliContextService);
  private readonly iDataService = inject(IntelliDataService);
  private readonly iDropdownOptionsService = inject(IntelliDropdownOptionsService);
  private readonly iExpressionsService = inject(IntelliExpressionsService);
  private readonly iQueryService = inject(IntelliQueryService);
  private readonly iRuntimeContextService = inject(IntelliRuntimeContextService);
  private readonly iSchemaService = inject(WorkflowSchemaCalculatorService);

  // Intellisense Config
  createConfigForAssignment = this.iConfigService.createConfigForAssignment.bind(
    this.iConfigService,
  );
  createConfigForColumn = this.iConfigService.createConfigForColumn.bind(this.iConfigService);
  createConfigForColumnIdOrName = this.iConfigService.createConfigForColumnIdOrName.bind(
    this.iConfigService,
  );
  createConfigForJoinCondition = this.iConfigService.createConfigForJoinCondition.bind(
    this.iConfigService,
  );
  createConfigForNodeInput = this.iConfigService.createConfigForNodeInput.bind(this.iConfigService);
  getConfigId = this.iConfigService.getConfigId.bind(this.iConfigService);

  // Intellisense Context
  createContext = this.iContextService.createContext.bind(this.iContextService);
  getContextForInput = this.iContextService.getContextForInput.bind(this.iContextService);

  // Data parsing
  getNestedValueFromInput = this.iDataService.getNestedValueFromInput.bind(this.iDataService);

  // Dropdown options for various workflow nodes
  getOptionsAndColumnsForElement = this.iDropdownOptionsService.getOptionsAndColumnsForElement.bind(
    this.iDropdownOptionsService,
  );
  getAssignedVarsOptions = this.iDropdownOptionsService.getAssignedVarsOptions.bind(
    this.iDropdownOptionsService,
  );
  getOptionsForAggregateFunctions =
    this.iDropdownOptionsService.getOptionsForAggregateFunctions.bind(this.iDropdownOptionsService);
  getOptionsForColumnsInDataset = this.iDropdownOptionsService.getOptionsForColumnsInDataset.bind(
    this.iDropdownOptionsService,
  );
  getOptionsForColumnsInSchema = this.iDropdownOptionsService.getOptionsForColumnsInSchema.bind(
    this.iDropdownOptionsService,
  );
  getOptionsForQueryOperators = this.iDropdownOptionsService.getOptionsForQueryOperators.bind(
    this.iDropdownOptionsService,
  );

  // Expression parsing
  canEvaluate = this.iExpressionsService.canEvaluate.bind(this.iExpressionsService);
  createAggregateFunction = this.iExpressionsService.createAggregateFunction.bind(
    this.iExpressionsService,
  );
  createAggregateFunctions = this.iExpressionsService.createAggregateFunctions.bind(
    this.iExpressionsService,
  );
  createConditions = this.iExpressionsService.createConditions.bind(this.iExpressionsService);
  createDatasetFilter = this.iExpressionsService.createDatasetFilter.bind(this.iExpressionsService);
  createDatasetQuery = this.iExpressionsService.createDatasetQuery.bind(this.iExpressionsService);
  eval = this.iExpressionsService.eval.bind(this.iExpressionsService);
  evalAsync = this.iExpressionsService.evalAsync.bind(this.iExpressionsService);
  evalExpressionObj = this.iExpressionsService.evalExpressionObj.bind(this.iExpressionsService);
  queryExpressionObj = this.iExpressionsService.queryExpressionObj.bind(this.iExpressionsService);
  getAggregateFunctionName = this.iExpressionsService.getAggregateFunctionName.bind(
    this.iExpressionsService,
  );
  normalizeLinkedParams = this.iExpressionsService.normalizeLinkedParams.bind(
    this.iExpressionsService,
  );
  tokenizeExpression = this.iExpressionsService.tokenizeExpression.bind(this.iExpressionsService);

  // Query service
  getComparisonOperatorName = this.iQueryService.getComparisonOperatorName.bind(this.iQueryService);
  isQueryValueRequired = this.iQueryService.isQueryValueRequired.bind(this.iQueryService);

  // Runtime Context
  createRuntimeContext = this.iRuntimeContextService.createRuntimeContext.bind(
    this.iRuntimeContextService,
  );
  getLocalVarsForResume = this.iRuntimeContextService.getLocalVarsForResume.bind(
    this.iRuntimeContextService,
  );
  prepareRuntimeContextForServer = this.iRuntimeContextService.prepareRuntimeContextForServer.bind(
    this.iRuntimeContextService,
  );
  updateRuntimeContext = this.iRuntimeContextService.updateRuntimeContext.bind(
    this.iRuntimeContextService,
  );
  updateRuntimeContextWithStoredVarsDefaults =
    this.iRuntimeContextService.updateRuntimeContextWithStoredVarsDefaults.bind(
      this.iRuntimeContextService,
    );

  updateRuntimeContextNamedOutput =
    this.iRuntimeContextService.updateRuntimeContextNamedOutput.bind(this.iRuntimeContextService);

  // Schema information
  getSchemaForAggregate = this.iSchemaService.getSchemaForAggregate.bind(this.iSchemaService);
  getSchemaForApiCall = this.iSchemaService.getSchemaForApiCall.bind(this.iSchemaService);
  getSchemaForCompute = this.iSchemaService.getSchemaForCompute.bind(this.iSchemaService);
  getSchemaForDataset = this.iSchemaService.getSchemaForDataset.bind(this.iSchemaService);
  getSchemaForDialog = this.iSchemaService.getSchemaForDialog.bind(this.iSchemaService);
  getSchemaForExpand = this.iSchemaService.getSchemaForExpand.bind(this.iSchemaService);
  getSchemaForGetUserLocation = this.iSchemaService.getSchemaForGetUserLocation.bind(
    this.iSchemaService,
  );
  getSchemaForJoin = this.iSchemaService.getSchemaForJoin.bind(this.iSchemaService);
  getSchemaForLookup = this.iSchemaService.getSchemaForLookup.bind(this.iSchemaService);
  getSchemaForSelectColumns = this.iSchemaService.getSchemaForSelectColumns.bind(
    this.iSchemaService,
  );
  calculateSchemaForWorkflow = this.iSchemaService.calculateSchemaForWorkflow.bind(
    this.iSchemaService,
  );
  getVariablesForWorkflow = this.iSchemaService.getVariablesForWorkflow.bind(this.iSchemaService);
  getSchemaForWorkflowAtStep = this.iSchemaService.getSchemaForWorkflowAtStep.bind(
    this.iSchemaService,
  );
  getWorkflowSchemaFromData = this.iSchemaService.getWorkflowSchemaFromData.bind(
    this.iSchemaService,
  );
  obtainSchema = this.iSchemaService.obtainSchema.bind(this.iSchemaService);
}
